/**
 * compositionService.ts
 *
 * Client-side computation of `Composition` lineage metadata for material nodes.
 * Called by action creators before inserting/updating nodes in the store so
 * that composition is present at save time and persisted to the v2 backend.
 *
 * Each composition object tracks:
 *  - `directContributions` — the immediate sources that feed this node
 *
 * Callers are responsible for calling the right helper based on what caused
 * the material to be created:
 *   computeInitialComposition   — standalone input material (no predecessors)
 *   propagateTransferComposition — TRANSFER/MERGE post-state materials
 *   propagateComplexComposition  — COMPLEX operation output materials
 *   propagateSimpleComposition   — TRANSFORM/MEASURE single-output materials
 */

import type { Composition, Quantity } from '../graph/domain/types';

// ─── Initial input node ──────────────────────────────────────────────────────

/**
 * For a brand-new material added to the graph with no predecessors.
 * Marks it as an entry-point so lineage traversal has a base case.
 */
export function computeInitialComposition(
  nodeId: string,
  nodeName: string,
): Composition {
  return {
    directContributions: [
      {
        fromNodeId: nodeId,
        fromNodeName: nodeName,
        contributionType: 'initial',
      },
    ],
  };
}

// ─── Transfer / Merge post-states ────────────────────────────────────────────

export interface TransferCompositionResult {
  sourcePostComposition: Composition;
  destPostComposition: Composition;
}

/**
 * After a TRANSFER or MERGE step:
 *  - Source post-state: retains what was NOT transferred (tracks source lineage)
 *  - Dest post-state:  gains what was transferred FROM the source PLUS the
 *    ongoing contents of the destination (pairEdge)
 *
 * @param sourceNodeId  vertexId of the source material (pre-state)
 * @param sourceName    display name of the source
 * @param destNodeId    vertexId of the destination material (pre-state)
 * @param destName      display name of the destination (undefined if draft)
 * @param operationId   vertexId / id of the TRANSFER operation
 * @param quantity      amount transferred, if known
 */
export function propagateTransferComposition(
  sourceNodeId: string,
  sourceName: string,
  destNodeId: string,
  destName: string | undefined,
  operationId: string,
  quantity?: Quantity,
): TransferCompositionResult {
  return {
    sourcePostComposition: {
      directContributions: [
        {
          fromNodeId: sourceNodeId,
          fromNodeName: sourceName,
          contributionType: 'transfer',
          quantity,
          stepId: operationId,
        },
      ],
    },
    destPostComposition: {
      directContributions: [
        {
          fromNodeId: sourceNodeId,
          fromNodeName: sourceName,
          contributionType: 'transfer',
          quantity,
          stepId: operationId,
        },
        {
          fromNodeId: destNodeId,
          fromNodeName: destName,
          contributionType: 'pairEdge',
          stepId: operationId,
        },
      ],
    },
  };
}

// ─── Complex operation outputs ───────────────────────────────────────────────

/**
 * For each output of a COMPLEX step: records which input node(s) contributed
 * to it via pairEdge links.
 *
 * @param inputNodeIds  vertexIds of all input materials to the COMPLEX step
 * @param operationId   vertexId / id of the COMPLEX operation
 */
export function propagateComplexComposition(
  inputNodeIds: string[],
  operationId: string,
): Composition {
  return {
    directContributions: inputNodeIds.map((fromNodeId) => ({
      fromNodeId,
      contributionType: 'pairEdge' as const,
      stepId: operationId,
    })),
  };
}

// ─── Simple (TRANSFORM / MEASURE) single-output ──────────────────────────────

/**
 * For a TRANSFORM or MEASURE post-state material: records the single upstream
 * source that flowed through the operation.
 *
 * @param sourceNodeId  vertexId of the input material
 * @param sourceName    display name of the input material
 * @param operationId   vertexId / id of the operation
 */
export function propagateSimpleComposition(
  sourceNodeId: string,
  sourceName: string,
  operationId: string,
): Composition {
  return {
    directContributions: [
      {
        fromNodeId: sourceNodeId,
        fromNodeName: sourceName,
        contributionType: 'transfer',
        stepId: operationId,
      },
    ],
  };
}

// ─── Complex operation with reminder nodes ──────────────────────────────────

/**
 * For a COMPLEX step with reminder nodes: records composition for both
 * output materials and reminder nodes.
 *
 * @param inputNodeIds Array of vertexIds of all input materials
 * @param inputNodeNames Array of display names for input materials
 * @param operationId vertexId of the COMPLEX operation
 * @returns Object with outputComposition (for outputs) and reminderCompositions (indexed by input index)
 */
export interface ComplexCompositionResult {
  outputComposition: Composition;
  reminderCompositions: Record<number, Composition>;
}

export function propagateComplexCompositionWithReminders(
  inputNodeIds: string[],
  inputNodeNames: string[],
  operationId: string,
): ComplexCompositionResult {
  // Output composition: all inputs contributed via pairEdge
  const outputComposition: Composition = {
    directContributions: inputNodeIds.map((id, idx) => ({
      fromNodeId: id,
      fromNodeName: inputNodeNames[idx],
      contributionType: 'pairEdge',
      stepId: operationId,
    })),
  };

  // Reminder compositions: each reminder tracks its corresponding input
  const reminderCompositions: Record<number, Composition> = {};
  for (let i = 0; i < inputNodeIds.length; i++) {
    reminderCompositions[i] = {
      directContributions: [
        {
          fromNodeId: inputNodeIds[i],
          fromNodeName: inputNodeNames[i],
          contributionType: 'complex_remainder',
          stepId: operationId,
        },
      ],
    };
  }

  return { outputComposition, reminderCompositions };
}
